import type { WarehouseZone } from "../types";
import { Line, Text } from "@react-three/drei";
import robotoMono from "../../elofusion/assets/fonts/RobotoMono-VariableFont_wght.ttf";
import { observer } from "mobx-react";
import { store } from "../../store";

const zoneColors: Record<WarehouseZone["type"], string> = {
    forbidden: "#ef4444",
    charging: "#22c55e",
    pickup: "#3b82f6",
    dropoff: "#a855f7",
    storage: "#f59e0b",
};

export const WarehouseZones = observer((): JSX.Element => {
    const zones = store.serviceAgv.warehouse.zones;

    return (
        <group>
            {zones.map((zone) => (
                <group key={zone.id}>
                    <mesh
                        key={zone.id}
                        position={[zone.x + zone.width / 2, 0.01, zone.z + zone.depth / 2]}
                        rotation={[-Math.PI / 2, 0, 0]}
                    >
                        <planeGeometry args={[zone.width, zone.depth]} />
                        <meshBasicMaterial
                            color={zoneColors[zone.type]}
                            transparent
                            opacity={0.25}
                        />
                    </mesh>
                    <Text
                        position={[zone.x + zone.width / 2, 0.02, zone.z + zone.depth / 2]}
                        rotation={[-Math.PI / 2, 0, 0]}
                        fontSize={0.6}
                        color={zoneColors[zone.type]}
                        anchorX="center"
                        anchorY="middle"
                        textAlign="center"
                        maxWidth={zone.width - 0.5}
                        font={robotoMono}
                    >
                        {zone.label}
                    </Text>
                </group>
            ))}
        </group>
    );
});

export const WarehouseFloor = observer((): JSX.Element => {
    const { floorWidth, floorDepth } = store.serviceAgv.warehouse;

    return (
        <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <planeGeometry args={[floorWidth, floorDepth]} />
            <meshStandardMaterial color="#e5e7eb" />
        </mesh>
    );
});

export const PathLines = observer((): JSX.Element => {
    const paths = store.serviceAgv.warehouse.paths;

    return (
        <group>
            {paths.map((path) => (
                <Line
                    key={path.id}
                    points={path.points.map((p) => [p.x, 0.02, p.z] as [number, number, number])}
                    color={path.color ?? "#eab308"}
                    lineWidth={2}
                    dashed
                    dashScale={3}
                    dashSize={0.5}
                    gapSize={0.3}
                />
            ))}
        </group>
    );
});

export const Obstacles = observer((): JSX.Element => {
    const obstacles = store.serviceAgv.warehouse.obstacles;

    return (
        <group>
            {obstacles.map((obs) => (
                <mesh
                    key={obs.id}
                    position={[obs.x + obs.width / 2, obs.height / 2, obs.z + obs.depth / 2]}
                >
                    <boxGeometry args={[obs.width, obs.height, obs.depth]} />
                    <meshStandardMaterial color="#9ca3af" />
                </mesh>
            ))}
        </group>
    );
});
